import React from 'react';
import { ExternalLink } from 'lucide-react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  onSupportButtonClick?: () => void;
}

function isExternalLink(url: string): boolean {
  // Internal links start with /, # or are relative paths
  if (url.startsWith('/') || url.startsWith('#') || !url.includes('://')) {
    return false;
  }

  // Check if the link is to the current domain
  try {
    const linkUrl = new URL(url);
    const currentDomain = window.location.hostname;
    return linkUrl.hostname !== currentDomain;
  } catch {
    // If URL parsing fails, treat as internal link
    return false;
  }
}

function parseMarkdownToHTML(text: string, includeSupportButton: boolean = false): string {
  let html = text;

  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');

  // Replace markdown links with appropriate HTML based on whether they're internal or external
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, (match, linkText, url) => {
    if (isExternalLink(url)) {
      // External link: open in new tab with icon
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium group"><span>${linkText}</span><svg class="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a>`;
    } else {
      // Internal link: same tab, no icon
      return `<a href="${url}" class="text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium">${linkText}</a>`;
    }
  });

  // Replace {{SUPPORT_BUTTON}} placeholder with inline text link
  if (includeSupportButton) {
    html = html.replace(/\{\{SUPPORT_BUTTON\}\}/g,
      '<button data-support-button class="inline text-[#FF6B35] hover:text-[#E55A2B] underline underline-offset-2 font-semibold transition-colors cursor-pointer">contact op te nemen met support</button>'
    );
  }

  html = html.replace(/\n\n/g, '</p><p class="mb-4">');
  html = html.replace(/\n/g, '<br>');

  if (!html.startsWith('<p')) {
    html = '<p class="mb-4">' + html;
  }
  if (!html.endsWith('</p>')) {
    html = html + '</p>';
  }

  return html;
}

export function MarkdownRenderer({ content, className = '', onSupportButtonClick }: MarkdownRendererProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hasSupportButton = content.includes('{{SUPPORT_BUTTON}}');
  const htmlContent = parseMarkdownToHTML(content, hasSupportButton);

  React.useEffect(() => {
    if (hasSupportButton && containerRef.current && onSupportButtonClick) {
      const button = containerRef.current.querySelector('[data-support-button]');
      if (button) {
        const handleClick = (e: Event) => {
          e.preventDefault();
          onSupportButtonClick();
        };
        button.addEventListener('click', handleClick);
        return () => button.removeEventListener('click', handleClick);
      }
    }
  }, [htmlContent, hasSupportButton, onSupportButtonClick]);

  return (
    <div
      ref={containerRef}
      className={`text-gray-800 leading-relaxed ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}

export function renderMarkdown(text: string): string {
  const hasSupportButton = text.includes('{{SUPPORT_BUTTON}}');
  return parseMarkdownToHTML(text, hasSupportButton);
}
